import { Component, OnInit, OnDestroy, ViewEncapsulation, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { MatCalendar } from '@angular/material/datepicker';
import { MatCard } from '@angular/material/card';
import { first } from 'lodash-es';
import { Observable, Subject, take, takeUntil } from 'rxjs';
import { CasualHireFormService } from '../../casual-hire-form.service';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { DateHelperService } from '@f3/services/date-helper.service';


@Component({
    selector: 'casual-hire-form-select-collection-date',
    templateUrl: './select-collection-date.component.html',
    styleUrls: ['./select-collection-date.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [MatCard, MatCalendar]
})
export class HireFormSelectCollectionDateComponent implements OnInit, OnDestroy {

    // @Public Properties
    public today = this._dateHelper.getToday();

    public tomorrow = this._dateHelper.getTomorrow();

    public months: { startAt: Date | null }[] = [];

    public calendarHeader = CalendarHeader

    public availableDates: string[] = [];

    public selectedDate: Date | null = null;

    // @Private Properties
    private _unsubscribeAll: Subject<any> = new Subject;
  
    private _selectedDate: { date: string, pickup?: string, dropoff?: string } | null = null


    /**
    * Constructor
    */
    constructor(
        private route: ActivatedRoute,
        private _casualHireFormService: CasualHireFormService,
        private _dateHelper: DateHelperService,
        private _changeDetectorRef: ChangeDetectorRef
    ) {
        this.dateIsSelectable = this.dateIsSelectable.bind(this);
    }


    //--------------------------------------------------------------------------------
    // @Lifecycle Hooks
    //--------------------------------------------------------------------------------

    /**
    * OnInit
    */
    ngOnInit(): void {
        this.route.data.subscribe(data => {
            this.availableDates = data['availableDates'];
            for(let i=0; i<6; i++) {
                const date = this._dateHelper.addMonths(this.today, i);
                this.months.push({
                    startAt: date
                })
            }
        });


        this._casualHireFormService.orderData$.pipe(
            take(1)
        ).subscribe({
            next: (orderData) => {
                
                this._selectedDate = orderData.collection
                console.log('Loaded collection data:', orderData.collection);
                
                // Update the public selectedDate property for the calendar
                if(orderData.collection && orderData.collection.date) {
                    const parts = orderData.collection.date.split('-');
                    if (parts.length === 3) {
                        this.selectedDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                        console.log('Set selectedDate property to:', this.selectedDate);
                    }
                    this._casualHireFormService.updateStepValidity(true)
                }
                // Trigger change detection to update the calendar
                this._changeDetectorRef.markForCheck();
            
            }
        })

    }

    /**
    * OnDestroy
    */
    ngOnDestroy() {
        this._casualHireFormService.updateStepValidity(false)

        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }


    //--------------------------------------------------------------------------------
    // @Accessors
    //--------------------------------------------------------------------------------



    //--------------------------------------------------------------------------------
    // @Public Methods
    //--------------------------------------------------------------------------------

    public dateSelected(date: any) {
        console.log('dateSelected called with:', date);
        console.log('Input date details:', {
            toString: date.toString(),
            toISOString: date.toISOString(),
            year: date.getFullYear(),
            month: date.getMonth(),
            date: date.getDate()
        });

        // Format date without timezone issues
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        
        console.log('Formatted dateStr:', dateStr);
        
        // Update the public selectedDate property
        this.selectedDate = date;
        console.log('Set this.selectedDate to:', this.selectedDate);
        
        const pickupStr = this._dateHelper.formatToDateTime(this._calculatePickupDate(date));
        const dropoffStr = this._dateHelper.formatToDateTime(this._calculateDropoffdate(date));

        let collection = {
            date: dateStr ?? '',
            pickup: pickupStr ?? '',
            dropoff: dropoffStr ?? ''
        }

        this._selectedDate = collection
        console.log('Setting _selectedDate to:', this._selectedDate);
        this._casualHireFormService.addToOrderData('collection', collection, dateStr ? true : false)
        
        // Trigger change detection
        this._changeDetectorRef.detectChanges();

    }

    public dateIsSelectable(d: any | null): boolean {
        if (!d) return false;
        // Format date without timezone issues
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        return this.availableDates?.includes(dateStr);
    }

    //--------------------------------------------------------------------------------
    // @Private Methods
    //--------------------------------------------------------------------------------

    /**
     * Calculate the pick up date
     */
    private _calculatePickupDate(date: Date) {
        return this._dateHelper.setTime(date, 10, 0, 0)
    }

    /**
     * Calculate the dropoff date
     */
    private _calculateDropoffdate(date: Date) {

        date = this._dateHelper.addDays(date, 7); // At least a week to return
        for(let i = 0; i <= 7; i++) {
            const d = this._dateHelper.addDays(date, i);
            const dayOfWeek = d.getDay(); // 0 = Sunday, 1 = Monday, 2 = Tuesday, 4 = Thursday

            if(dayOfWeek === 2 || dayOfWeek === 4) { // Tuesday or Thursday
                return this._dateHelper.setTime(d, 14, 0, 0);
            }
        }

        return null
    }

    /**
     * Highlight available dates in the calendar
     */
    /**
     * Date class function for Material Calendar
     * Returns CSS class for dates that should be highlighted
     */
    dateClass = (date: Date): string => {
        // Use UTC to prevent timezone shifting
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;
        const isAvailable = this.availableDates.includes(dateString);
        return isAvailable ? 'available-date' : '';
    }

}


@Component({
    selector: 'datepicker-header',
    template: `
        <div class="pb-3">
            {{ dateLabel }}
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CalendarHeader<D> implements OnDestroy {

    // @Private Properties
    private _unsubscribeAll: Subject<any> = new Subject


    /**
     * Constructor
     */
    constructor (
        private _calendar: MatCalendar<D>,
        private _dateAdapter: DateAdapter<D>,
        private _changeDetectorRef: ChangeDetectorRef
    ) {

        _calendar.stateChanges.pipe(
            takeUntil(this._unsubscribeAll)
        ).subscribe({
            next: () => {
                this._changeDetectorRef.markForCheck()
            }
        })

    }


    //--------------------------------------------------------------------------------
    // @Lifecycle Hooks
    //--------------------------------------------------------------------------------

    /**
     * ON Destroy
     */
    ngOnDestroy(): void {
        this._unsubscribeAll.next(null)
        this._unsubscribeAll.complete()
    }


    //--------------------------------------------------------------------------------
    // @Accessors
    //--------------------------------------------------------------------------------

    get dateLabel() {
        const formatter = new Intl.DateTimeFormat('en-NZ', {
            month: 'short',
            year: 'numeric'
        });
        return formatter.format(this._calendar.activeDate as any);
    }

}